/**
 * ============================================================================
 *  LAUNDRY VENTURES — CENTRAL CONTENT CONFIGURATION
 * ============================================================================
 *  This is the single source of truth for all copy, links, images and
 *  placeholders on the site. Edit values here — do NOT hard-code copy inside
 *  components.
 *
 *  HOW TO REPLACE PLACEHOLDER ASSETS
 *  --------------------------------------------------------------------------
 *  1. LOGO      -> replace /public/logo-laundry-ventures.webp
 *  2. IMAGES    -> swap any `images.*` URL below with your own hosted photo
 *                  (interior renderings, machine photography, materials, etc.)
 *  3. STATS     -> anything wrapped as `{ value: "TBD", ... }` is a placeholder.
 *                  Replace `value` with confirmed figures when available.
 *  4. CONTACT   -> update `contact` block (email / phone / socials).
 *  5. VIDEO/3D  -> `media.heroVideo` and `media.model3d` are integration points
 *                  for future lazy-loaded video / Three.js assets.
 * ============================================================================
 */

export const brand = {
  parent: "Laundry Ventures",
  concept: "Laundry Lounge",
  logo: "/logo-transparent.png",
};

export const nav = [
  { label: "Vision", href: "#vision" },
  { label: "Laundry Lounge", href: "#lounge" },
  { label: "Experience", href: "#experience" },
  { label: "Ventures", href: "#ventures" },
  { label: "Partner", href: "#partner" },
  { label: "Contact", href: "#contact" },
];

export const hero = {
  overline: "Laundry Ventures",
  // rendered line-by-line with a masked reveal
  headlineLines: ["Laundry,", "reimagined."],
  body:
    "Laundry Ventures is building thoughtful, modern laundry spaces designed around comfort, community and a better customer experience.",
  ctaPrimary: { label: "Scroll to explore", href: "#top" },
  ctaSecondary: { label: "Partner with us", href: "#partner" },
  scrollHint: "Scroll to enter",
};

export const marquee = [
  "Hospitality",
  "Architecture",
  "Comfort",
  "Craft",
  "Community",
];

export const oldExperience = {
  overline: "Section 01 — The old way",
  headline: "Laundry has been treated like a chore for too long.",
  body:
    "Harsh lighting. Dated machines. Nowhere comfortable to sit. For decades, the laundromat has been a place people wanted to leave as quickly as possible.",
  points: ["Uncomfortable rooms", "Little to no seating", "Dated, unreliable machines", "Poorly lit, poorly designed"],
};

export const transformation = {
  overline: "Section 01 — The shift",
  before: "For too long, laundry has been treated like a chore.",
  headline: "So we designed a place where people would enjoy their stay.",
  body:
    "Laundry Lounge brings hospitality-level thinking into an everyday service. Every detail is designed to make the experience easier, warmer and more comfortable — so a necessary weekly errand becomes a genuinely good hour.",
  quote: "Hospitality is the operating strategy, not just the finish package.",
};

export const experience = {
  overline: "Section 03 — The Laundry Lounge experience",
  headline: "An attended space, designed for the wait.",
  intro:
    "The Lounge pairs commercial equipment with a design-led environment and an attendant on every shift. Not a room full of identical machines — a place that respects your time.",
  chapters: [
    {
      no: "01",
      title: "Commercial equipment",
      copy:
        "Reliable Speed Queen machines, including oversized 60–80 lb washers built for comforters, sleeping bags and camping gear.",
    },
    {
      no: "02",
      title: "Comfortable seating",
      copy:
        "Dedicated lounge, reading and work stations — comfortable spots designed for the hour you actually spend here.",
    },
    {
      no: "03",
      title: "Attended & safe",
      copy:
        "Staffed during every open hour, with corner-to-corner lighting and card-controlled private restrooms for paying customers.",
    },
    {
      no: "04",
      title: "Always cleaning",
      copy:
        "Machine wiping, hourly floor sweeps and nightly deep cleans keep the space genuinely clean, not just presentable.",
    },
    {
      no: "05",
      title: "Frictionless payment",
      copy:
        "Modern, cashless payment and self-service coffee, vending, free high-speed WiFi and USB / USB-C charging throughout.",
    },
    {
      no: "06",
      title: "Community-centered",
      copy:
        "A welcoming flow built for renters, families, students and travelers — a room the neighborhood is glad to have.",
    },
  ],
};

export const designDetails = {
  overline: "Section 04 — Design details",
  headline: "Every detail changes how the space feels.",
  body:
    "Warm wood, deep navy, soft cream, brushed metal and considered lighting. Real materials, chosen for how they feel — not just how they photograph.",
  panels: [
    { label: "Coffee bar", tag: "Amenity", key: "wood" },
    { label: "Lounge areas", tag: "Space", key: "navy" },
    { label: "Eco-friendly washers", tag: "Equipment", key: "steel" },
    { label: "Folding space", tag: "Service", key: "light" },
  ],
};

// Section 05 — clearly marked placeholders. Replace `value` when confirmed.
export const ventures = {
  overline: "Section 05 — Laundry Ventures",
  headline: "Built for the customer. Designed to scale.",
  body:
    "Laundry Ventures combines customer experience, thoughtful design, location strategy and operational discipline to build modern laundry businesses that people actually want to return to.",
  stats: [
    { label: "Flagship location", value: "Meridian, ID", note: "In development" },
    { label: "Floor area", value: "3,200", unit: "sq ft" },
    { label: "Machine capacity", value: "60–80", unit: "lb washers" },
    { label: "Attended", value: "Every", unit: "open hour" },
  ],
};

export const partner = {
  overline: "Section 06 — Partnership",
  headline: "The next Laundry Lounge starts with the right partnership.",
  body:
    "We work with partners who share the long view. If you own the right space, or you want to build modern laundry businesses with us, we'd like to talk.",
  audiences: ["Investors", "Property owners", "Landlords", "Developers", "Strategic partners"],
};

export const contact = {
  overline: "Contact",
  headline: "Let's talk.",
  email: "joseph@laundryventures.com",
  emailSubject: "Laundry Ventures — Partnership Inquiry",
};

export const footer = {
  columns: [
    {
      title: "Company",
      links: [
        { label: "Vision", href: "#vision" },
        { label: "Laundry Lounge", href: "#lounge" },
        { label: "Ventures", href: "#ventures" },
      ],
    },
    {
      title: "Explore",
      links: [
        { label: "The experience", href: "#experience" },
        { label: "Design details", href: "#design" },
        { label: "Partner with us", href: "#partner" },
      ],
    },
  ],
  social: [
    { label: "Instagram", href: "#" }, // placeholder
    { label: "LinkedIn", href: "#" }, // placeholder
  ],
  legal: [
    { label: "Privacy Policy", href: "#" }, // placeholder
    { label: "Terms", href: "#" }, // placeholder
  ],
  copyright: `© ${new Date().getFullYear()} Laundry Ventures LLC. All rights reserved.`,
};

// ---------------------------------------------------------------------------
//  IMAGERY  — premium placeholders. Swap URLs with real photography/renderings.
// ---------------------------------------------------------------------------
export const images = {
  // Soapy stainless-steel drum — client reference (hero fly-through)
  heroDrum: "/hero-drum.png",
  // Bright Laundry Lounge arrival (emergence frame)
  heroEmerge: "/mk4.png",
  oldExperience:
    "https://images.unsplash.com/photo-1743428471505-8391bc8c088a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  // Real Laundry Lounge mockup renderings (client-provided)
  transformation: "/lounge-interior.png",
  experienceWide: "/mk4.png",
  detail: {
    wood: "/mk2.png",
    navy: "/lounge-interior.png",
    steel: "/mk1.png",
    light: "/mk3.png",
  },
  towels: "/mk3.png",
  venturesBg: "/mk4.png",
};

// Integration points for future lazy-loaded heavy media
export const media = {
  heroVideo: null, // e.g. "/assets/hero-drum.mp4" — lazy-loaded when provided
  model3d: null, // e.g. "/assets/washer.glb" — R3F scene mount point
};
